import { Box, Flex, FormControl, FormHelperText, FormLabel, Text } from "@chakra-ui/react";
import { FieldTemplateProps, ObjectFieldTemplateProps, TemplatesType } from "@rjsf/utils";
import { AllHTMLAttributes } from "react";
import { FormattedMessage } from "react-intl";

import { AddIcon, RanaButton, TrashIcon } from "@nens/ranalib";

const CustomObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  return (
    <Box
      display="grid"
      gridGap={6}
      sx={{
        "*[data-disabled]": { opacity: "1 !important" },
        "*[disabled]": { opacity: "1 !important" },
      }}
    >
      {props.properties.map(({ content }) => content)}
    </Box>
  );
};

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const { id, classNames, label, help, required, rawDescription, rawErrors, children, schema } =
    props;

  const hasChildren =
    [...(schema.anyOf ?? []), ...(schema.oneOf ?? [])].filter(
      (property) => typeof property === "object" && property.type !== "null"
    ).length > 1;
  // Ignore anyOf or oneOf because it renders weird errors of values not matching schema's
  // before you have even filled in anything
  const errors = !hasChildren ? [...new Set(rawErrors)] : [];

  return (
    <FormControl
      {...(hasChildren && {
        border: "1px solid",
        borderColor: "brand.border",
        p: 4,
        rounded: "base",
      })}
      position="relative"
      isRequired={required}
      className={classNames}
      isInvalid={Boolean(rawErrors?.length)}
    >
      <Flex
        {...(schema.type === "boolean"
          ? { flexDirection: "row-reverse", alignItems: "center", gap: 4 }
          : { flexDirection: "column" })}
        justifyContent="flex-end"
      >
        <Box gap={2}>
          {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
          {rawDescription && (
            <Box textStyle="sm" fontWeight="light" fontFamily="roboto" color="brand.grey.600">
              {rawDescription}
            </Box>
          )}
        </Box>
        <Box h="fit">{children}</Box>
      </Flex>
      {help && <FormHelperText>{help}</FormHelperText>}
      {/* Cant be FormErrorMessage because that doesn't render, use Set so you remove duplicates */}
      {errors?.map((error, index) => (
        <Text key={index} color="semantic.error.main" textStyle="sm">
          {error}
        </Text>
      ))}
    </FormControl>
  );
};

type OverrideTemplates<T = Partial<TemplatesType>> = Partial<TemplatesType> & T;

// FUTURE move these to some default location
export const TEMPLATES: OverrideTemplates<{
  ButtonTemplates?: Partial<TemplatesType["ButtonTemplates"]>;
}> = {
  ObjectFieldTemplate: CustomObjectFieldTemplate,
  FieldTemplate: CustomFieldTemplate,
  DescriptionFieldTemplate: () => null,
  TitleFieldTemplate: () => null,
  ButtonTemplates: {
    AddButton: (props: AllHTMLAttributes<HTMLButtonElement>) => (
      <RanaButton
        isDisabled={props.disabled}
        variant="secondary"
        onClick={props.onClick}
        leftIcon={<AddIcon boxSize={6} />}
      >
        <FormattedMessage id="button.add" defaultMessage="Add" />
      </RanaButton>
    ),
    SubmitButton: () => undefined,
    CopyButton: (props: AllHTMLAttributes<HTMLButtonElement>) => (
      <RanaButton isDisabled={props.disabled} variant="tertiary" onClick={props.onClick}>
        <FormattedMessage id="button.copy" defaultMessage="Copy" />
      </RanaButton>
    ),
    MoveDownButton: (props: AllHTMLAttributes<HTMLButtonElement>) => (
      <RanaButton isDisabled={props.disabled} variant="tertiary" onClick={props.onClick}>
        <FormattedMessage id="button.down" defaultMessage="Down" />
      </RanaButton>
    ),
    MoveUpButton: (props: AllHTMLAttributes<HTMLButtonElement>) => (
      <RanaButton isDisabled={props.disabled} variant="tertiary" onClick={props.onClick}>
        <FormattedMessage id="button.up" defaultMessage="Up" />
      </RanaButton>
    ),
    RemoveButton: (props: AllHTMLAttributes<HTMLButtonElement>) => (
      <RanaButton isDisabled={props.disabled} w="button" variant="tertiary" onClick={props.onClick}>
        <TrashIcon boxSize={6} />
      </RanaButton>
    ),
  },
};
